"use client"

import { useSiteConfig } from "@/lib/site-config"
import { useSiteAssets } from "@/hooks/use-site-assets"
import { useI18n } from "@/src/i18n/provider"
import { Button } from "@/components/ui/button"
import { MessageCircle, Copy, Loader2, Send, Mail } from "lucide-react"
import { useState, useMemo } from "react"
import { useToast } from "@/hooks/use-toast"

export function ContactUs() {
  const staticConfig = useSiteConfig()
  const { assets, isLoading } = useSiteAssets()
  const { t } = useI18n()
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)

  // Merge API + static config
  const config = useMemo(() => {
    const apiContact = assets.routing?.contact
    const lineUrl = apiContact?.lineUrl || staticConfig.lineUrl || ""
    const lineQrUrl = apiContact?.lineQrUrl || ""
    const telegramUrl = apiContact?.telegramUrl || staticConfig.telegramUrl || ""
    const email = apiContact?.supportEmail || staticConfig.email || ""

    // Try to extract LINE ID from URL (works for line.me/*/p/@xxx format)
    const lineIdMatch = lineUrl.match(/@[\w.-]+/)
    const lineId = lineIdMatch ? lineIdMatch[0] : ""

    return {
      lineUrl, lineQrUrl, lineId, telegramUrl, email,
      showLine: apiContact?.showLine !== false,
      showTelegram: apiContact?.showTelegram !== false,
      showEmail: apiContact?.showEmail !== false,
    }
  }, [assets.routing?.contact, staticConfig])

  const copyLineUrl = () => {
    const textToCopy = config.lineId || config.lineUrl
    navigator.clipboard.writeText(textToCopy)
    setCopied(true)
    toast({
      title: t("contact.copied"),
      description: t("contact.copiedDesc"),
    })
    setTimeout(() => setCopied(false), 2000)
  }

  const openLine = () => {
    window.open(config.lineUrl, "_blank")
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-background pb-20 lg:pb-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    )
  }

  return (
    <div className="bg-background pb-20 lg:pb-6">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <h1 className="text-2xl font-bold text-theme-surface-foreground">{t("contact.title")}</h1>
          <p className="text-sm text-theme-muted mt-1">{t("contact.subtitle")}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 max-w-2xl space-y-4">
        {/* LINE Section */}
        {config.showLine && (
          <div className="bg-gradient-to-br from-[#06C755]/10 to-[#06C755]/5 border-2 border-[#06C755] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-[#06C755] rounded-full flex items-center justify-center">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-theme-surface-foreground">{t("contact.lineTitle")}</h2>
                <p className="text-sm text-theme-muted">{t("contact.lineDesc")}</p>
              </div>
            </div>

            {config.lineId && (
              <div className="bg-white/80 dark:bg-gray-900/80 rounded-lg p-4 mb-4">
                <p className="text-xs text-theme-muted mb-1">LINE ID</p>
                <div className="flex items-center justify-between gap-2">
                  <code className="text-lg font-mono font-bold text-theme-surface-foreground">{config.lineId}</code>
                  <Button variant="outline" size="sm" onClick={copyLineUrl}>
                    {copied ? "✓" : <Copy className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            )}

            {config.lineQrUrl && (
              <div className="bg-card rounded-lg p-4 mb-4 flex justify-center">
                <div className="text-center">
                  <div className="h-48 w-48 bg-muted rounded-lg flex items-center justify-center mb-2 overflow-hidden">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={config.lineQrUrl}
                      alt="LINE QR Code"
                      width={192}
                      height={192}
                      className="rounded-lg object-contain"
                    />
                  </div>
                  <p className="text-xs text-theme-muted">{t("contact.scanQr")}</p>
                </div>
              </div>
            )}

            <Button onClick={openLine} className="w-full bg-[#06C755] hover:bg-[#05B04B] text-white h-12 text-base">
              <MessageCircle className="h-5 w-5 mr-2" />
              {t("contact.addLine")}
            </Button>
          </div>
        )}

        {/* Telegram Section */}
        {config.showTelegram && config.telegramUrl && (
          <div className="bg-gradient-to-br from-[#0088cc]/10 to-[#0088cc]/5 border-2 border-[#0088cc] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-[#0088cc] rounded-full flex items-center justify-center">
                <Send className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-theme-surface-foreground">Telegram</h2>
                <p className="text-sm text-theme-muted">{t("contact.lineDesc")}</p>
              </div>
            </div>
            <Button
              onClick={() => window.open(config.telegramUrl, "_blank")}
              className="w-full bg-[#0088cc] hover:bg-[#006da3] text-white h-12 text-base"
            >
              <Send className="h-5 w-5 mr-2" />
              Telegram
            </Button>
          </div>
        )}

        {/* Email Section */}
        {config.showEmail && config.email && (
          <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-2 border-blue-500 rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 bg-blue-500 rounded-full flex items-center justify-center">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-theme-surface-foreground">Email</h2>
                <p className="text-sm text-theme-muted">{config.email}</p>
              </div>
            </div>
            <Button
              onClick={() => window.open(`mailto:${config.email}`, "_blank")}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white h-12 text-base"
            >
              <Mail className="h-5 w-5 mr-2" />
              {t("contact.title")}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
